<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {BlogPostLayout, Hero} from '@react-spectrum/docs';
export default BlogPostLayout;

---
description: This release includes new nightly releases and support for SSR. Huge thanks to all of the amazing contributors!
date: 2020-09-02
---

# September 02, 2020 Release

We have some exciting changes in this release. First of all, we now have nightly builds! Thanks to @mischnic, we now release our packages whenever we merge to main under a “nightly” tag. This means you don’t have to wait for our normal release cycle to get a preview of what we are up to. In this release we also worked on adding support for SSR. This has been an ask from a number of people who are using react-spectrum with popular products/setups/projects such as Gatsby and NextJs. For more details on SSR, visit our [docs](https://react-spectrum.adobe.com/react-spectrum/ssr.html). As always, a huge thank you to all our contributors for the new features, fixes and updates.

## New components

- **CheckboxGroup** - [@Andarist](https://github.com/Andarist) - [PR](https://github.com/adobe/react-spectrum/pull/868)
    - [CheckboxGroup](../../react-spectrum/CheckboxGroup.html) component in `@react-spectrum/checkbox`
    - [useCheckboxGroup](../../react-aria/useCheckboxGroup.html) hook in `@react-aria/checkbox`
    - [useCheckboxGroupState](../../react-stately/useCheckboxGroupState.html) hook in `@react-stately/checkbox`
- **SSRProvider** - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/992)
    - [SSRProvider](../../react-aria/SSRProvider.html) component in `@react-aria/ssr`
    - [useIsSSR](../../react-aria/useIsSSR.html.html) component in `@react-aria/ssr`

## New features

- `DialogTrigger` close function now works with Popovers - [@cheapsteak](https://github.com/cheapsteak) - [PR](https://github.com/adobe/react-spectrum/pull/997)
- Nightly builds - [@mischnic](https://github.com/mischnic) - [PR](https://github.com/adobe/react-spectrum/pull/735)


## Bug fixes

- Move rAF in `Virtualizer` to avoid calling on ssr - [@fezproof](https://github.com/fezproof) - [PR](https://github.com/adobe/react-spectrum/pull/975)
- Update type in `useLink` - [@ronderksen](https://github.com/ronderksen) - [PR](https://github.com/adobe/react-spectrum/pull/1000)
- Updates to `Slider` - [@chungwu](https://github.com/chungwu) - [PR](https://github.com/adobe/react-spectrum/pull/955)
- Basic SSR tests passing with CI integration - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/994)
- Use screen width instead of window to determine `isMobile` - [@michayou](https://github.com/michayou) -  [PR](https://github.com/adobe/react-spectrum/pull/1001)
- Add extra padding in `Tray` to help with iOS home bar - [@ronderksen](https://github.com/ronderksen) - [PR](https://github.com/adobe/react-spectrum/pull/1007)
- Set a default for long and dir when SSR - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1026)
- Stop `Tray` closing on scroll - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1023)
- Stop `Slider` firing when value hasn’t changed - [@xitter](https://github.com/xitter) - [PR](https://github.com/adobe/react-spectrum/pull/999)

## Docs

- Update `useSwitch` example to pass a ref - [@andrew-t-james](https://github.com/andrew-t-james) - [PR](https://github.com/adobe/react-spectrum/pull/988)
- Added clarification for `FocusRing` when modality is undefined - [@tomByrer](https://github.com/tomByrer) - [PR](https://github.com/adobe/react-spectrum/pull/1022)

## Released packages

```
- @adobe/react-spectrum@3.3.0

```
